import Component from "@glimmer/component";
import { cached, tracked } from "@glimmer/tracking";
import { action } from "@ember/object";
import { getOwner } from "@ember/owner";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import willDestroy from "@ember/render-modifiers/modifiers/will-destroy";
import { cancel, next } from "@ember/runloop";
import { service } from "@ember/service";
import { popupAjaxError } from "discourse/lib/ajax-error";
import discourseDebounce from "discourse/lib/debounce";
import { bind } from "discourse/lib/decorators";
import { NotificationLevels } from "discourse/lib/notification-levels";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import { i18n } from "discourse-i18n";
import ChatThreadTitlePrompt from "discourse/plugins/chat/discourse/components/chat-thread-title-prompt";
import firstVisibleMessageId from "discourse/plugins/chat/discourse/helpers/first-visible-message-id";
import ChatChannelThreadSubscriptionManager from "discourse/plugins/chat/discourse/lib/chat-channel-thread-subscription-manager";
import {
  FUTURE,
  PAST,
  READ_INTERVAL_MS,
} from "discourse/plugins/chat/discourse/lib/chat-constants";
import ChatMessagesLoader from "discourse/plugins/chat/discourse/lib/chat-messages-loader";
import ChatPaneState from "discourse/plugins/chat/discourse/lib/chat-pane-state";
import DatesSeparatorsPositioner from "discourse/plugins/chat/discourse/lib/dates-separators-positioner";
import { extractCurrentTopicInfo } from "discourse/plugins/chat/discourse/lib/extract-current-topic-info";
import {
  maintainScrollPosition,
  scrollListToBottom,
  scrollListToMessage,
  scrollListToTop,
} from "discourse/plugins/chat/discourse/lib/scroll-helpers";
import ChatMessage from "discourse/plugins/chat/discourse/models/chat-message";
import UserChatThreadMembership from "discourse/plugins/chat/discourse/models/user-chat-thread-membership";
import ChatComposerThread from "./chat/composer/thread.js";
import ChatScrollToBottomArrow from "./chat/scroll-to-bottom-arrow.gjs";
import ChatSelectionManager from "./chat/selection-manager.gjs";
import ChatChannelPreviewCard from "./chat-channel-preview-card.gjs";
import Message from "./chat-message.gjs";
import ChatMessagesContainer from "./chat-messages-container.gjs";
import ChatMessagesScroller from "./chat-messages-scroller.gjs";
import ChatSkeleton from "./chat-skeleton.gjs";
import ChatThreadHeading from "./chat-thread-heading.gjs";
import ChatUploadDropZone from "./chat-upload-drop-zone.gjs";

export default class ChatThread extends Component {
  @service capabilities;
  @service chatApi;
  @service chatDraftsManager;
  @service chatNewMessageAnnouncer;
  @service chatThreadComposer;
  @service chatThreadPane;
  @service dialog;
  @service currentUser;
  @service siteSettings;

  @tracked atBottom = true;
  @tracked isScrolling = false;
  @tracked uploadDropZone;

  scroller = null;

  paneState = new ChatPaneState(getOwner(this), {
    contextKey: this.pendingContextKey,
    onUserPresent: this.maybeDebouncedUpdateLastReadMessage,
  });

  @cached
  get messagesLoader() {
    return new ChatMessagesLoader(getOwner(this), this.args.thread);
  }

  get messagesManager() {
    return this.args.thread.messagesManager;
  }

  get showChannelPreview() {
    const channel = this.args.thread.channel;
    return channel?.isCategoryChannel && !channel?.isFollowing;
  }

  get pendingContextKey() {
    return this.args.thread?.id ? `thread:${this.args.thread.id}` : null;
  }

  @action
  registerScroller(element) {
    this.scroller = element;
  }

  @action
  setup(element) {
    this.uploadDropZone = element;

    this.messagesManager.clear();
    if (this.currentUser) {
      this.args.thread.draft =
        this.chatDraftsManager.get(
          this.args.thread.channel?.id,
          this.args.thread.id
        ) ||
        ChatMessage.createDraftMessage(this.args.thread.channel, {
          user: this.currentUser,
          thread: this.args.thread,
        });
      this.chatThreadComposer.focus();
    }
    this.loadMessages();
  }

  @action
  teardown() {
    this.paneState.teardown();
    this.subscriptionManager.teardown();
    cancel(this._debouncedFillPaneAttemptHandler);
    cancel(this._debouncedUpdateLastReadMessageHandler);
  }

  @action
  onScroll(state) {
    next(() => {
      if (this.#flushIgnoreNextScroll()) {
        return;
      }

      DatesSeparatorsPositioner.apply(this.scroller);
      this.paneState.updatePendingContentFromScrollState({
        scroller: this.scroller,
        fetchedOnce: this.messagesLoader.fetchedOnce,
        canLoadMoreFuture: this.messagesLoader.canLoadMoreFuture,
        state,
      });
      this.isScrolling = true;
      this.maybeDebouncedUpdateLastReadMessage();

      if (
        state.atTop ||
        (!this.capabilities.isIOS &&
          state.up &&
          state.distanceToTop.percentage < 40)
      ) {
        this.fetchMoreMessages({ direction: PAST });
      } else if (state.atBottom) {
        this.fetchMoreMessages({ direction: FUTURE });
      }
    });
  }

  @action
  onScrollEnd(state) {
    this.isScrolling = false;
    this.atBottom = state.atBottom;
    this.paneState.updateLiveEdgeFromScrollState(state);

    if (state.atBottom) {
      // Visible but unfocused panes can passively live-follow. Clear their
      // pending affordance at the bottom while keeping hidden/away panes pending.
      if (this.paneState.userIsPresent) {
        this.paneState.clearPendingMessages();
      }
      this.fetchMoreMessages({ direction: FUTURE });
    } else {
      this.paneState.updatePendingContentFromScrollState({
        scroller: this.scroller,
        fetchedOnce: this.messagesLoader.fetchedOnce,
        canLoadMoreFuture: this.messagesLoader.canLoadMoreFuture,
        state,
      });
    }
  }

  @bind
  maybeDebouncedUpdateLastReadMessage() {
    if (this.paneState.shouldMarkRead()) {
      this.debouncedUpdateLastReadMessage();
    }
  }

  @bind
  debouncedUpdateLastReadMessage() {
    this._debouncedUpdateLastReadMessageHandler = discourseDebounce(
      this,
      this.updateLastReadMessage,
      READ_INTERVAL_MS
    );
  }

  @bind
  updateLastReadMessage() {
    if (!this.paneState.shouldMarkRead()) {
      return;
    }

    if (!this.args.thread?.currentUserMembership) {
      return;
    }

    const firstFullyVisibleMessageId = firstVisibleMessageId(this.scroller);
    if (!firstFullyVisibleMessageId) {
      return;
    }

    const firstMessage = this.messagesManager.findMessage(
      firstFullyVisibleMessageId
    );
    if (!firstMessage) {
      return;
    }

    const lastReadId = this.args.thread.currentUserMembership.lastReadMessageId;
    if (lastReadId >= firstMessage.id) {
      return;
    }

    return this.chatApi.markThreadAsRead(
      this.args.thread.channel.id,
      this.args.thread.id,
      firstMessage.id
    );
  }

  @action
  loadMessages() {
    this.fetchMessages();
    this.subscriptionManager = new ChatChannelThreadSubscriptionManager(
      this,
      this.args.thread,
      {
        onNewMessage: this.onNewMessage,
        onRemoveMessage: (message) =>
          this.messagesLoader.releaseMessage(message),
      }
    );
  }

  @action
  didResizePane() {
    this._ignoreNextScroll = true;
    this.debounceFillPaneAttempt();
    this.maybeDebouncedUpdateLastReadMessage();
    DatesSeparatorsPositioner.apply(this.scroller);

    this.paneState.updatePendingContentFromScrollerPosition({
      scroller: this.scroller,
      fetchedOnce: this.messagesLoader.fetchedOnce,
      canLoadMoreFuture: this.messagesLoader.canLoadMoreFuture,
    });
  }

  async fetchMessages(findArgs = {}) {
    if (this.messagesLoader.loading) {
      return;
    }

    this.messagesManager.clear();

    findArgs.target_message_id ??=
      findArgs.targetMessageId ||
      this.args.targetMessageId ||
      this.args.thread.currentUserMembership?.lastReadMessageId;

    if (!findArgs.target_message_id) {
      findArgs.direction = FUTURE;
    }

    const result = await this.messagesLoader.load(findArgs);
    if (!result) {
      return;
    }

    const [messages, meta] = this.processMessages(this.args.thread, result);
    this.messagesManager.addMessages(messages);
    this.args.thread.details = meta;

    if (meta.target_message_id) {
      this.scrollToMessageId(meta.target_message_id, { highlight: true });
    } else {
      this.scrollToTop();
    }

    this.debounceFillPaneAttempt();
  }

  @action
  async fetchMoreMessages({ direction }) {
    if (this.messagesLoader.loading) {
      return;
    }

    const result = await this.messagesLoader.loadMore({ direction });
    if (!result) {
      return;
    }

    // messages held while newer history was unloaded are part of this page
    if (direction === FUTURE && !this.messagesLoader.canLoadMoreFuture) {
      this.paneState.clearPendingMessages();
    }

    this.paneState.updatePendingContentFromScrollerPosition({
      scroller: this.scroller,
      fetchedOnce: this.messagesLoader.fetchedOnce,
      canLoadMoreFuture: this.messagesLoader.canLoadMoreFuture,
    });

    // a request for newer messages made while this page loaded was dropped
    if (direction === PAST && this.paneState.isAtLiveEdge) {
      this.fetchMoreMessages({ direction: FUTURE });
    }

    const [messages, meta] = this.processMessages(this.args.thread, result);
    if (!messages?.length) {
      return;
    }

    if (direction === FUTURE) {
      maintainScrollPosition(this.scroller, () =>
        this.messagesManager.addMessages(messages)
      );
    } else {
      this.messagesManager.addMessages(messages);
    }
    this.args.thread.details = meta;

    if (direction === PAST) {
      this.scrollToMessageId(messages.at(-1).id);
    }

    this.debounceFillPaneAttempt();
  }

  @action
  async scrollToLatestMessage() {
    if (this.messagesLoader.canLoadMoreFuture) {
      await this.fetchMessages({
        target_message_id: this.args.thread.lastMessageId,
      });
    } else if (this.messagesManager.messages.length > 0) {
      await this.scrollToBottom();
    }
  }

  debounceFillPaneAttempt() {
    if (!this.messagesLoader.fetchedOnce) {
      return;
    }

    this._debouncedFillPaneAttemptHandler = discourseDebounce(
      this,
      this.fillPaneAttempt,
      500
    );
  }

  async fillPaneAttempt() {
    // safeguard
    if (this.messagesManager.messages.length > 200) {
      return;
    }

    if (!this.messagesLoader.canLoadMorePast) {
      return;
    }

    const firstMessage = this.messagesManager.messages[0];
    if (!firstMessage?.visible) {
      return;
    }

    await this.fetchMoreMessages({ direction: PAST });
  }

  scrollToMessageId(
    messageId,
    opts = { highlight: false, position: "start", autoExpand: false }
  ) {
    this._ignoreNextScroll = true;
    const message = this.messagesManager.findMessage(messageId);
    scrollListToMessage(this.scroller, message, opts);
  }

  @bind
  onNewMessage(message) {
    const isOwnMessage = message.user.id === this.currentUser.id;
    this.messagesLoader.trackLiveMessage(message);

    if (!isOwnMessage) {
      this.chatNewMessageAnnouncer.notify(message, {
        visible: this.paneState.isDocumentVisible,
        active: this.paneState.isActiveReader,
      });
    }

    // the load in flight, or the one that already ran, brings it in
    if (
      !this.messagesLoader.fetchedOnce ||
      this.messagesManager.findMessage(message.id)
    ) {
      return;
    }

    this.paneState.handleIncomingMessage({
      scroller: this.scroller,
      shouldAutoScroll: this.paneState.shouldAutoScrollIncomingMessage({
        isAtLiveEdge: this.paneState.isAtLiveEdge,
        isOwnMessage,
      }),
      canLoadMoreFuture: this.messagesLoader.canLoadMoreFuture,
      addMessage: () => this.messagesManager.addMessages([message]),
      onAutoAdd: () => this.maybeDebouncedUpdateLastReadMessage(),
      isOwnMessage,
    });
  }

  @bind
  processMessages(thread, result) {
    const messages = result.messages.map((messageData) => {
      const ignored = this.currentUser?.ignored_users || [];
      const hidden = ignored.includes(messageData.user.username);

      return ChatMessage.create(thread.channel, {
        ...messageData,
        hidden,
        expanded: !(hidden || messageData.deleted_at),
        manager: this.messagesManager,
        thread,
      });
    });

    return [messages, result.meta];
  }

  @action
  async onSendMessage(message) {
    if (
      message.message.length > this.siteSettings.chat_maximum_message_length
    ) {
      this.dialog.alert(
        i18n("chat.message_too_long", {
          count: this.siteSettings.chat_maximum_message_length,
        })
      );
      return;
    }

    await message.cook();
    if (message.editing) {
      await this.#sendEditMessage(message);
    } else {
      await this.#sendNewMessage(message);
    }
  }

  @bind
  fetchMessagesByDate(date) {
    if (this.messagesLoader.loading) {
      return;
    }

    const message = this.messagesManager.findFirstMessageOfDay(
      new Date(date),
      this.currentUser.user_option.timezone
    );
    if (message.firstOfResults && this.messagesLoader.canLoadMorePast) {
      this.fetchMessages({ target_date: date, direction: FUTURE });
    } else {
      this.highlightOrFetchMessage(message.id, { position: "center" });
    }
  }

  @action
  highlightOrFetchMessage(messageId, options = {}) {
    const message = this.messagesManager.findMessage(messageId);
    if (message) {
      this.scrollToMessageId(
        message.id,
        Object.assign(
          {
            highlight: true,
            position: "start",
            autoExpand: true,
            behavior: this.capabilities.isIOS ? "smooth" : null,
          },
          options
        )
      );
    } else {
      this.fetchMessages({ target_message_id: messageId });
    }
  }

  @action
  resetComposerMessage() {
    this.args.thread.draft = ChatMessage.createDraftMessage(
      this.args.thread.channel,
      {
        user: this.currentUser,
        thread: this.args.thread,
      }
    );
  }

  @action
  async scrollToBottom() {
    this._ignoreNextScroll = true;
    await scrollListToBottom(this.scroller);
    if (this.paneState.shouldMarkRead()) {
      this.debouncedUpdateLastReadMessage();
    }
    this.paneState.clearPendingMessages();
  }

  @action
  async scrollToTop() {
    this._ignoreNextScroll = true;
    await scrollListToTop(this.scroller);
  }

  @action
  resendStagedMessage(stagedMessage) {
    this.chatThreadPane.sending = true;

    stagedMessage.error = null;

    this.chatApi
      .sendMessage(this.args.thread.channel.id, {
        cooked: stagedMessage.cooked,
        message: stagedMessage.message,
        upload_ids: stagedMessage.uploads.map((upload) => upload.id),
        staged_id: stagedMessage.id,
        thread_id: this.args.thread.id,
      })
      .catch((error) => {
        stagedMessage.setSendError(error);
      })
      .finally(() => {
        this.chatThreadPane.sending = false;
      });
  }

  async #sendNewMessage(message) {
    if (this.chatThreadPane.sending) {
      return;
    }

    this.chatThreadPane.sending = true;
    this._ignoreNextScroll = true;
    await this.args.thread.stageMessage(message);
    this.resetComposerMessage();

    if (!this.messagesLoader.canLoadMoreFuture) {
      this.scrollToLatestMessage();
    }

    try {
      const response = await this.chatApi.sendMessage(
        this.args.thread.channel.id,
        {
          message: message.message,
          in_reply_to_id: null,
          staged_id: message.id,
          upload_ids: message.uploads.map((upload) => upload.id),
          thread_id: message.thread.id,
          ...extractCurrentTopicInfo(this),
        }
      );

      this.args.thread.currentUserMembership ??=
        UserChatThreadMembership.create({
          notification_level: NotificationLevels.TRACKING,
          last_read_message_id: response.message_id,
        });

      this.scrollToLatestMessage();
    } catch (error) {
      this.#onSendError(message.id, error);
    } finally {
      this.chatDraftsManager.remove(
        this.args.thread.channel.id,
        this.args.thread.id
      );
      this.chatThreadPane.sending = false;
    }
  }

  async #sendEditMessage(message) {
    this.chatThreadPane.sending = true;

    const data = {
      message: message.message,
      upload_ids: message.uploads.map((upload) => upload.id),
    };

    this.resetComposerMessage();

    try {
      return await this.chatApi.editMessage(
        message.channel.id,
        message.id,
        data
      );
    } catch (e) {
      popupAjaxError(e);
    } finally {
      this.chatDraftsManager.remove(
        this.args.thread.channel.id,
        this.args.thread.id
      );
      this.chatThreadPane.sending = false;
    }
  }

  #onSendError(stagedId, error) {
    const stagedMessage =
      this.args.thread.messagesManager.findStagedMessage(stagedId);
    stagedMessage?.setSendError(error);

    this.resetComposerMessage();
  }

  #flushIgnoreNextScroll() {
    const prev = this._ignoreNextScroll;
    this._ignoreNextScroll = false;
    return prev;
  }

  <template>
    <div
      class={{dConcatClass
        "chat-thread"
        (if this.messagesLoader.loading "--loading")
        (if this.messagesLoader.fetchedOnce "--loaded")
      }}
      data-id={{@thread.id}}
      {{didInsert this.setup}}
      {{willDestroy this.teardown}}
    >
      <ChatMessagesScroller
        @onRegisterScroller={{this.registerScroller}}
        @onScroll={{this.onScroll}}
        @onScrollEnd={{this.onScrollEnd}}
      >
        <ChatMessagesContainer @didResizePane={{this.didResizePane}}>
          {{#each this.messagesManager.messages key="id" as |message|}}
            <Message
              @context="thread"
              @disableMouseEvents={{this.isScrolling}}
              @fetchMessagesByDate={{this.fetchMessagesByDate}}
              @message={{message}}
              @resendStagedMessage={{this.resendStagedMessage}}
            />
          {{/each}}

          {{#unless this.messagesLoader.fetchedOnce}}
            {{#if this.messagesLoader.loading}}
              <ChatSkeleton />
            {{/if}}
          {{/unless}}
        </ChatMessagesContainer>

        <ChatThreadHeading @thread={{@thread}} />
      </ChatMessagesScroller>

      <ChatScrollToBottomArrow
        @isVisible={{this.paneState.hasPendingContentBelow}}
        @onScrollToBottom={{this.scrollToLatestMessage}}
      />

      {{#if this.chatThreadPane.selectingMessages}}
        <ChatSelectionManager
          @channel={{@thread.channel}}
          @messagesManager={{this.messagesManager}}
          @pane={{this.chatThreadPane}}
        />
      {{else}}
        {{#if this.showChannelPreview}}
          <ChatChannelPreviewCard @channel={{@thread.channel}} />
        {{else}}
          <ChatComposerThread
            @channel={{@channel}}
            @onSendMessage={{this.onSendMessage}}
            @scroller={{this.scroller}}
            @thread={{@thread}}
            @uploadDropZone={{this.uploadDropZone}}
          />
        {{/if}}
      {{/if}}

      <ChatUploadDropZone @model={{@thread}} />
      <ChatThreadTitlePrompt @thread={{@thread}} />
    </div>
  </template>
}
